import { useEffect, useState, useRef } from 'react';
import type { KanbanEvento } from '@pieltec/contracts';
import {
  FALHAS_ANTES_DA_SONDAGEM,
  atrasoReconexaoMs,
  classificarSondagemSse,
  interpretarEvento,
} from '../../../lib/kanban/sse';

export type SseStatus = 'conectado' | 'reconectando' | 'nao_autorizado';

export interface UseKanbanSseProps {
  url?: string;
  /**
   * Resolução dinâmica de URL (ex.: ticket HMAC curto a cada conexão).
   * O hook guarda a função mais recente em ref, então uma arrow inline não reabre o stream a cada
   * render; só a presença/ausência do resolvedor (ou a `url`) reinicia a conexão.
   * Se devolver null, o stream é considerado não autorizado e para.
   */
  resolverUrl?: () => Promise<string | null>;
  onEvento?: (evento: KanbanEvento) => void;
  onReconectar?: () => void;
  /** Stream recusado com 401/403 (sessão expirada, acesso negado ou dispositivo revogado): a reconexão para. */
  onNaoAutorizado?: () => void;
}

export interface ConexaoKanbanSse {
  encerrar: () => void;
}

export function iniciarConexaoKanbanSse(
  props: UseKanbanSseProps,
  notificarStatus?: (status: SseStatus) => void,
): ConexaoKanbanSse {
  let sse: EventSource | null = null;
  let timeout: ReturnType<typeof setTimeout> | undefined;
  let isReconnecting = false;
  let falhas = 0;
  let encerrado = false;
  let sondagem: AbortController | null = null;

  async function obterUrlAtual(): Promise<string | null> {
    if (props.resolverUrl) {
      return props.resolverUrl();
    }
    return props.url ?? null;
  }

  // Após falhas seguidas, uma requisição GET cabeça-a-cabeça revela o status HTTP do stream, que o
  // EventSource esconde. 401/403 encerram as tentativas em vez de martelar a API para sempre.
  async function estaNaoAutorizado(): Promise<boolean> {
    sondagem = new AbortController();
    try {
      const targetUrl = await obterUrlAtual();
      if (targetUrl === null) return true;
      if (encerrado) return false;
      const resposta = await fetch(targetUrl, {
        credentials: 'include',
        headers: { Accept: 'text/event-stream' },
        signal: sondagem.signal,
      });
      sondagem.abort(); // só o status interessa; não consome o stream
      return classificarSondagemSse(resposta.status) === 'nao_autorizado';
    } catch {
      return false;
    }
  }

  async function connect(): Promise<void> {
    let targetUrl: string | null;
    try {
      targetUrl = await obterUrlAtual();
    } catch {
      // Falha ao obter a URL/ticket (rede, 5xx, API reiniciando): reconecta com backoff em vez de
      // virar rejeição não tratada e deixar o tempo real morto (CR-04). `null` segue sendo "não autorizado".
      if (encerrado) return;
      notificarStatus?.('reconectando');
      isReconnecting = true;
      falhas += 1;
      timeout = setTimeout(() => {
        if (!encerrado) void connect();
      }, atrasoReconexaoMs(falhas));
      return;
    }
    if (encerrado) return;
    if (targetUrl === null) {
      notificarStatus?.('nao_autorizado');
      props.onNaoAutorizado?.();
      return;
    }

    sse = new EventSource(targetUrl, { withCredentials: true });

    sse.onopen = () => {
      falhas = 0;
      if (isReconnecting) {
        isReconnecting = false;
        props.onReconectar?.();
      }
      notificarStatus?.('conectado');
    };

    sse.onmessage = (event) => {
      const payload = typeof event.data === 'string' ? interpretarEvento(event.data) : null;
      if (payload) {
        props.onEvento?.(payload);
      }
    };

    sse.onerror = () => {
      sse?.close();
      notificarStatus?.('reconectando');
      isReconnecting = true;
      falhas += 1;
      const reconectar = () => {
        timeout = setTimeout(() => {
          if (!encerrado) void connect();
        }, atrasoReconexaoMs(falhas));
      };
      if (falhas < FALHAS_ANTES_DA_SONDAGEM) {
        reconectar();
        return;
      }
      void estaNaoAutorizado().then((negado) => {
        if (encerrado) return;
        if (negado) {
          notificarStatus?.('nao_autorizado');
          props.onNaoAutorizado?.();
          return;
        }
        reconectar();
      });
    };
  }

  void connect();

  return {
    encerrar: () => {
      encerrado = true;
      if (timeout) clearTimeout(timeout);
      sondagem?.abort();
      if (sse) sse.close();
    },
  };
}

export function useKanbanSse({ url, resolverUrl, onEvento, onReconectar, onNaoAutorizado }: UseKanbanSseProps) {
  const [status, setStatus] = useState<SseStatus>('conectado');
  const onEventoRef = useRef(onEvento);
  const onReconectarRef = useRef(onReconectar);
  const onNaoAutorizadoRef = useRef(onNaoAutorizado);
  const resolverUrlRef = useRef(resolverUrl);
  const temResolverUrl = resolverUrl !== undefined;

  useEffect(() => {
    onEventoRef.current = onEvento;
    onReconectarRef.current = onReconectar;
    onNaoAutorizadoRef.current = onNaoAutorizado;
    resolverUrlRef.current = resolverUrl;
  }, [onEvento, onReconectar, onNaoAutorizado, resolverUrl]);

  useEffect(() => {
    const conexao = iniciarConexaoKanbanSse(
      {
        url,
        resolverUrl: temResolverUrl ? () => resolverUrlRef.current?.() ?? Promise.resolve(null) : undefined,
        onEvento: (ev) => onEventoRef.current?.(ev),
        onReconectar: () => onReconectarRef.current?.(),
        onNaoAutorizado: () => onNaoAutorizadoRef.current?.(),
      },
      setStatus,
    );
    return () => conexao.encerrar();
  }, [url, temResolverUrl]);

  return { status };
}
